/**
 * Shared vector tile layer utilities.
 * Used by both VectorTileMap (standalone) and useVectorTileDynamicLayers (embedded in AnalysisMap).
 */

/**
 * Generate a pill-shaped image as raw pixel data for use with
 * Mapbox icon-text-fit. Uses stretchX so the rounded ends stay
 * crisp while the middle stretches to fit any text width.
 */
export const addPillImage = (map: any, imageId: string, fillColor: string) => {
  if (map.hasImage(imageId)) return

  const w = 40
  const h = 20
  const r = 6

  const hex = fillColor.replace('#', '')
  const cr = parseInt(hex.substring(0, 2), 16)
  const cg = parseInt(hex.substring(2, 4), 16)
  const cb = parseInt(hex.substring(4, 6), 16)

  const data = new Uint8Array(w * h * 4)
  for (let py = 0; py < h; py++) {
    for (let px = 0; px < w; px++) {
      let inside = true

      const dx = px < r ? r - px : px > w - r - 1 ? px - (w - r - 1) : 0
      const dy = py < r ? r - py : py > h - r - 1 ? py - (h - r - 1) : 0
      if (dx > 0 && dy > 0) {
        inside = dx * dx + dy * dy <= r * r
      }

      if (inside) {
        const i = (py * w + px) * 4
        data[i] = cr
        data[i + 1] = cg
        data[i + 2] = cb
        data[i + 3] = 255
      }
    }
  }

  map.addImage(
    imageId,
    { width: w, height: h, data },
    {
      stretchX: [[r, w - r]],
      stretchY: [[r, h - r]],
      content: [r, r, w - r, h - r],
    }
  )
}

/**
 * Add a vector tile source to the map.
 */
export const setupVTSource = (map: any, sourceId: string, tileUrl: string, maxzoom = 24) => {
  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: 'vector',
      tiles: [tileUrl],
      minzoom: 0,
      maxzoom,
    })
  }
}

export interface VTPointLayerConfig {
  color: string
  size: number
  strokeColor: string
  strokeWidth: number
  clusterEnabled?: boolean
  clusterMaxZoom?: number
}

/**
 * Add the main circle point layer for vector tile features.
 * When clustering is enabled, points fade in above the cluster threshold.
 */
export const setupVTPointLayer = (
  map: any,
  layerId: string,
  sourceId: string,
  sourceLayer: string,
  config: VTPointLayerConfig,
  beforeLayerId?: string
) => {
  if (map.getLayer(layerId)) return

  const {
    color,
    size,
    strokeColor,
    strokeWidth,
    clusterEnabled = false,
    clusterMaxZoom = 13,
  } = config

  const paint: Record<string, any> = {
    'circle-color': [
      'case',
      ['boolean', ['feature-state', 'selected'], false],
      '#1a1a1a',
      ['boolean', ['feature-state', 'hover'], false],
      color,
      color,
    ],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, size * 0.5, 15, size, 22, size * 1.5],
    'circle-stroke-width': [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      Math.max(strokeWidth, 2),
      ['boolean', ['feature-state', 'selected'], false],
      Math.max(strokeWidth, 2),
      strokeWidth,
    ],
    'circle-stroke-color': [
      'case',
      ['boolean', ['feature-state', 'selected'], false],
      '#ffffff',
      ['boolean', ['feature-state', 'hover'], false],
      '#ffffff',
      strokeColor,
    ],
  }

  if (clusterEnabled) {
    paint['circle-opacity'] = [
      'interpolate',
      ['linear'],
      ['zoom'],
      clusterMaxZoom - 2,
      0,
      clusterMaxZoom,
      1,
    ]
  }

  map.addLayer(
    {
      id: layerId,
      type: 'circle',
      source: sourceId,
      'source-layer': sourceLayer,
      paint,
    },
    beforeLayerId
  )
}

export interface VTClusterConfig {
  aggsSourceLayer: string
  maxZoom: number
  clusterColor: string
  minRadius: number
  maxRadius: number
}

/**
 * Add cluster circle and count label layers.
 */
export const setupVTClusterLayers = (
  map: any,
  baseLayerId: string,
  sourceId: string,
  config: VTClusterConfig
) => {
  const clusterLayerId = `${baseLayerId}-cluster`
  const clusterCountLayerId = `${baseLayerId}-cluster-count`

  const { aggsSourceLayer, maxZoom, clusterColor, minRadius, maxRadius } = config

  if (!map.getLayer(clusterLayerId)) {
    map.addLayer({
      id: clusterLayerId,
      type: 'circle',
      source: sourceId,
      'source-layer': aggsSourceLayer,
      maxzoom: maxZoom + 1,
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['get', '_count'],
          1,
          minRadius,
          1000,
          maxRadius,
        ],
        'circle-color': clusterColor,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], maxZoom - 1, 1, maxZoom + 1, 0],
      },
    })
  }

  if (!map.getLayer(clusterCountLayerId)) {
    map.addLayer({
      id: clusterCountLayerId,
      type: 'symbol',
      source: sourceId,
      'source-layer': aggsSourceLayer,
      maxzoom: maxZoom + 1,
      filter: ['>=', ['get', '_count'], 2],
      layout: {
        'text-field': ['number-format', ['get', '_count'], { locale: 'de-CH' }],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#ffffff',
        'text-opacity': ['interpolate', ['linear'], ['zoom'], maxZoom - 1, 1, maxZoom + 1, 0],
      },
    })
  }
}

/**
 * Add a heatmap layer for vector tile aggregation features.
 */
export const setupVTHeatmapLayer = (
  map: any,
  sourceId: string,
  layerId: string,
  aggsSourceLayer: string,
  maxZoom: number,
  colorRamp?: any[]
) => {
  if (map.getLayer(layerId)) return

  const defaultColorRamp = [
    'interpolate',
    ['linear'],
    ['heatmap-density'],
    0,
    'rgba(0, 45, 90, 0)',
    0.1,
    'rgba(0, 45, 90, 0.35)',
    0.25,
    'rgba(0, 65, 120, 0.5)',
    0.4,
    'rgba(0, 100, 170, 0.6)',
    0.55,
    'rgba(0, 140, 200, 0.7)',
    0.7,
    'rgba(2, 148, 223, 0.8)',
    0.85,
    'rgba(130, 210, 240, 0.85)',
    1,
    'rgba(200, 235, 255, 0.9)',
  ]

  // Opacity fades across the two zoom levels after `maxZoom`. The layer has
  // to stay mounted for that whole span: aggregation tiles are still the only
  // features at `maxZoom`, and individual points arrive on the next tile.
  const heatmapMaxZoom = maxZoom + 2

  map.addLayer({
    id: layerId,
    type: 'heatmap',
    source: sourceId,
    'source-layer': aggsSourceLayer,
    maxzoom: heatmapMaxZoom,
    paint: {
      'heatmap-weight': [
        'interpolate',
        ['linear'],
        ['get', '_count'],
        1,
        0.15,
        20,
        0.4,
        100,
        0.7,
        500,
        1,
      ],
      'heatmap-intensity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        0,
        0.5,
        7,
        0.6,
        9,
        0.6,
        10,
        0.6,
        11,
        0.6,
        12,
        0.5,
        13,
        0.7,
      ],
      'heatmap-color': colorRamp || defaultColorRamp,
      'heatmap-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        0,
        8,
        7,
        10,
        8,
        14,
        9,
        18,
        10,
        18,
        11,
        18,
        12,
        15,
        13,
        40,
      ],
      'heatmap-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        maxZoom,
        0.8,
        maxZoom + 1,
        0.4,
        heatmapMaxZoom,
        0,
      ],
    },
  })
}

export interface VTLabelConfig {
  field: string
  backgroundColor: string
  textColor: string
  fontSize: number
  minZoom: number
  pill: boolean
  textFieldExpression?: any[]
}

/**
 * Build a Mapbox expression for compact K/M price formatting with truncation.
 */
export const buildCompactTextField = (field: string, maxTextLen = 14) => {
  return [
    'case',
    ['all', ['==', ['typeof', ['get', field]], 'number'], ['>=', ['get', field], 1000000]],
    ['concat', ['to-string', ['/', ['round', ['/', ['get', field], 100000]], 10]], 'M'],
    ['all', ['==', ['typeof', ['get', field]], 'number'], ['>=', ['get', field], 1000]],
    ['concat', ['to-string', ['/', ['round', ['/', ['get', field], 100]], 10]], 'K'],
    ['>', ['length', ['to-string', ['get', field]]], maxTextLen],
    ['concat', ['slice', ['to-string', ['get', field]], 0, maxTextLen], '...'],
    ['to-string', ['get', field]],
  ]
}

/**
 * Add label symbol layer (pill mode or halo text mode).
 */
export const setupVTLabelLayer = (
  map: any,
  labelLayerId: string,
  sourceId: string,
  sourceLayer: string,
  config: VTLabelConfig,
  pillImageId?: string
) => {
  if (map.getLayer(labelLayerId)) return

  const { field, backgroundColor, textColor, fontSize, minZoom, pill, textFieldExpression } = config
  const compactTextField = textFieldExpression || buildCompactTextField(field)

  const filter = ['all', ['has', field], ['!=', ['get', field], ''], ['!=', ['get', field], 0]]

  if (pill && pillImageId) {
    map.addLayer({
      id: labelLayerId,
      type: 'symbol',
      source: sourceId,
      'source-layer': sourceLayer,
      minzoom: minZoom,
      filter,
      layout: {
        'icon-image': pillImageId,
        'icon-text-fit': 'both' as const,
        'icon-text-fit-padding': [0, 3, 0, 3],
        'icon-allow-overlap': false,
        'text-field': compactTextField,
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': fontSize,
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': textColor,
      },
    })
  } else {
    map.addLayer({
      id: labelLayerId,
      type: 'symbol',
      source: sourceId,
      'source-layer': sourceLayer,
      minzoom: minZoom,
      layout: {
        'text-field': compactTextField,
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': fontSize,
        'text-offset': [0, -1.5],
        'text-anchor': 'center',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': textColor,
        'text-halo-color': backgroundColor,
        'text-halo-width': 4,
        'text-halo-blur': 0,
      },
    })
  }
}

/**
 * Query and deduplicate features at a given point across the specified layers.
 */
export const queryUniqueFeatures = (
  map: any,
  point: { x: number; y: number },
  layerIds: string[]
): any[] => {
  const existingLayers = layerIds.filter((id) => map.getLayer(id))
  if (existingLayers.length === 0) return []

  const allFeatures = map.queryRenderedFeatures(point, {
    layers: existingLayers,
  })

  const seen = new Set()
  return allFeatures.filter((f: any) => {
    const id = f.properties?.id ?? f.id
    if (id == null || seen.has(id)) return false
    seen.add(id)
    return true
  })
}

/**
 * Safely remove a list of layers and sources from the map.
 */
export const cleanupVTLayers = (
  map: any,
  layerIds: string[],
  sourceIds: string[],
  imageIds: string[] = []
) => {
  layerIds.forEach((id) => {
    try {
      if (map.getLayer(id)) map.removeLayer(id)
    } catch {
      // ignore
    }
  })
  sourceIds.forEach((id) => {
    try {
      if (map.getSource(id)) map.removeSource(id)
    } catch {
      // ignore
    }
  })
  imageIds.forEach((id) => {
    try {
      if (map.hasImage(id)) map.removeImage(id)
    } catch {
      // ignore
    }
  })
}
